import React, { useState, useEffect, useId, ReactNode, ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, Collapse, Tooltip, Typography } from 'antd';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
interface AccordionProps {
  disabled?: boolean;
  expanded?: boolean;
  onChange?: (checked: boolean) => void;
  tooltip?: string;
  label: string | ReactNode;
  field?: string;
  isCollapsibleByCheckbox?: boolean;
  children?: ReactNode;
  required?: boolean;
  actionCode?: string;
}

const StyledCheckbox = styled(Checkbox, {
  shouldForwardProp: (prop) => prop !== 'disabled'
})`
  ${({ disabled }) =>
    disabled &&
    `
    cursor: not-allowed;

    .ant-checkbox,
    .ant-checkbox-input {
      cursor: not-allowed;
    }

    .ant-checkbox .ant-checkbox-inner,
    &:hover .ant-checkbox .ant-checkbox-inner {
      background: rgba(0, 0, 0, 0.04) !important;
      border-color: #d9d9d9 !important;

      &:after {
        border-color: rgba(0, 0, 0, 0.25) !important;
      }
    }

    .ant-checkbox:after {
      display: none;
    }
  `}
`;

/** As only one Collapse.Panel is rendered in each Accordion, we can hard-code one key */
const KEY = 'accordion';

export const Accordion = ({
  expanded = false,
  disabled,
  onChange,
  tooltip,
  label,
  field,
  isCollapsibleByCheckbox = true,
  children,
  required,
  actionCode
}: AccordionProps) => {
  const { t } = useTranslation('common');
  const [isExpanded, setIsExpanded] = useState(expanded);
  const labelId = useId();
  const requiredId = useId();

  useEffect(() => {
    setIsExpanded(expanded);
  }, [expanded]);

  const checkboxProps: ComponentProps<typeof Checkbox> = {
    checked: isExpanded,
    // aria-disabled instead of disabled keeps the checkbox focusable, so it's announced as unavailable rather than skipped
    onChange: (e) => {
      if (disabled) {
        return;
      }
      // If onChange prop provided then expanded state is handled externally
      // If onChange prop is not provided, component handles expanded state internally
      if (onChange) {
        onChange(e.target.checked);
      } else {
        setIsExpanded(e.target.checked);
      }
    }
  };

  const checkboxAriaProps = {
    'aria-labelledby': labelId,
    'aria-describedby': required ? requiredId : undefined,
    'aria-disabled': disabled || undefined
  };

  const toggleTestId = `accordion-checkbox-${field || actionCode}`;

  return (
    <Collapse
      destroyOnHidden
      collapsible="icon"
      activeKey={(isExpanded && children) || !isCollapsibleByCheckbox ? KEY : ''}
      css={css`
        margin-bottom: 1em;
      `}
    >
      <Collapse.Panel
        key={KEY}
        showArrow={false}
        header={
          <span
            css={css`
              display: flex;
              align-items: center;
              gap: 12px;
            `}
          >
            {isCollapsibleByCheckbox &&
              (tooltip ? (
                <Tooltip title={tooltip} trigger={['hover', 'focus']}>
                  <StyledCheckbox disabled={disabled} {...checkboxProps} {...checkboxAriaProps} />
                </Tooltip>
              ) : (
                <StyledCheckbox
                  disabled={disabled}
                  data-testid={toggleTestId}
                  {...checkboxProps}
                  {...checkboxAriaProps}
                />
              ))}
            <span id={labelId}>{label}</span>
          </span>
        }
        extra={
          required && (
            <Typography.Text id={requiredId} type="danger">
              {t('Required')}
            </Typography.Text>
          )
        }
        data-unavailable={disabled && !required}
        css={css`
          .ant-collapse-header {
            padding: 6px 10px !important;
          }
          &[data-unavailable='true'] {
            .ant-collapse-header {
              color: var(--lightProductGray);

              background-color: #fff;
            }
          }
        `}
      >
        {children}
      </Collapse.Panel>
    </Collapse>
  );
};
